import { apiClient } from "./api-client";

const unwrapPayload = <T>(payload: unknown, fallback: T): T => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    const nested = (payload as { data?: T }).data;
    if (nested !== undefined) {
      return nested;
    }
  }

  return (payload as T) ?? fallback;
};

export interface UpdateUserProfilePayload {
  username?: string;
  name?: string;
  email?: string | null;
  country?: string;
  gender?: string;
  phoneNumber?: string | null;
  currentPassword?: string;
  newPassword?: string;
  spokenLanguages?: string[];
  contributionLanguages?: string[];
  moderationLanguages?: string[];
  preferredLocale?: 'fr' | 'en';
}
export interface DataExportRequest {
  userId: string | number;
  requestType: 'full' | 'audio' | 'images' | 'metadata';
  email?: string;
}

export interface AccountDeletionRequest {
  userId: number;
  reason?: string;
  email?: string;
  /** Current password: the backend refuses the deletion without it. */
  password: string;
}

export interface AccountDeletionResponse {
  success: boolean;
  message: string;
  requestId?: string;
  deletionDate?: string;
}

export const userDataApi = {
  /**
   * Data copy (GDPR): built on the spot, emailed as a JSON attachment when the
   * account has an address, otherwise `delivery: 'download'`.
   */
  requestDataExport: async (request: DataExportRequest): Promise<{
    success: boolean;
    delivery: 'email' | 'download';
    email?: string;
  }> => {
    const { data } = await apiClient.post('/user/data-export', request);
    const payload = unwrapPayload<Record<string, unknown>>(data, {});
    return {
      success: payload.success !== false,
      delivery: payload.delivery === 'email' ? 'email' : 'download',
      email: typeof payload.email === 'string' ? payload.email : undefined,
    };
  },

  /** Saves the JSON copy through the browser. */
  downloadDataExport: async (): Promise<void> => {
    const response = await apiClient.get('/user/data-export/download', { responseType: 'blob' });
    const disposition = String(response.headers?.['content-disposition'] ?? '');
    const fileName = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'umbaji-my-data.json';
    const url = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  /**
   * Account deletion (GDPR, right to erasure): immediate, after the current
   * password; the backend answers 401 WRONG_PASSWORD otherwise.
   * @param request - Deletion request details
   */
  requestAccountDeletion: async (request: AccountDeletionRequest): Promise<AccountDeletionResponse> => {
    const { data } = await apiClient.post('/user/account-deletion', request);
    const payload = unwrapPayload<Record<string, unknown>>(data, {});

    return {
      success: payload.success !== false,
      message:
        typeof payload.message === 'string' && payload.message.trim()
          ? payload.message
          : 'Account deletion request accepted',
      requestId: typeof payload.requestId === 'string' ? payload.requestId : undefined,
      deletionDate:
        typeof payload.deletionDate === 'string'
          ? payload.deletionDate
          : typeof payload.deletedAt === 'string'
            ? payload.deletedAt
            : undefined,
    };
  },

  /**
   * Check status of data export request
   * @param requestId - The request ID returned when export was requested
   */
  checkExportStatus: async (requestId: string): Promise<{
    status: 'pending' | 'processing' | 'ready' | 'expired';
    downloadUrl?: string;
    expiresAt?: string;
    progress?: number;
  }> => {
    const { data } = await apiClient.get(`/user/data-export/${requestId}/status`);
    return unwrapPayload(data, {
      status: 'pending' as const,
    });
  },

  /**
   * Check status of account deletion request
   * @param requestId - The request ID returned when deletion was requested
   */
  checkDeletionStatus: async (requestId: string): Promise<{
    status: 'pending' | 'processing' | 'completed' | 'cancelled';
    scheduledDate?: string;
    canCancel: boolean;
  }> => {
    const { data } = await apiClient.get(`/user/account-deletion/${requestId}/status`);
    return unwrapPayload(data, {
      status: 'pending' as const,
      canCancel: false,
    });
  },

  /**
   * Cancel account deletion request (if still pending)
   * @param requestId - The request ID to cancel
   */
  cancelDeletionRequest: async (requestId: string): Promise<{
    success: boolean;
    message: string;
  }> => {
    const { data } = await apiClient.post(`/user/account-deletion/${requestId}/cancel`);
    return unwrapPayload(data, {
      success: true,
      message: 'Deletion request cancelled',
    });
  },

  /**
   * Get user's data statistics
   * @param userId - The user ID
   */
  getUserDataStats: async (userId: string | number): Promise<{
    audioRecordings: number;
    images: number;
    transcriptions: number;
    validations: number;
    totalStorageUsed: string;
    accountCreatedAt: string;
    lastActivityAt: string;
  }> => {
    const { data } = await apiClient.get(`/user/${userId}/data-stats`);
    return unwrapPayload(data, {
      audioRecordings: 0,
      images: 0,
      transcriptions: 0,
      validations: 0,
      totalStorageUsed: '0 B',
      accountCreatedAt: '',
      lastActivityAt: '',
    });
  },

  /**
   * Update current user's profile
   * @param username - username in URL
   * @param dto - partial update payload
   */
  updateUser: async (username: string, dto: UpdateUserProfilePayload): Promise<unknown> => {
    const { data } = await apiClient.put(`/users/user/${encodeURIComponent(username)}`, dto);
    return data;
  },
};
